import React, { useMemo, useRef } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { VStack, Text, Button, Separator } from "@chakra-ui/react"
import { ActivityCreateProps, ActivityCreateScreen } from "./screens"
import {
  useCreateFormData,
  useActivityWorkflowActions,
  useLatestBatch,
} from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import ActivityCreateScreenWrapper from "./ActivityCreateScreenWrapper"
import { ActivityAttributesConfigurable, ActivityAttributesConfigurableRef } from "../ActivityAttributesConfigurable"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { DateTime, utc, zone } from "timezonecomplete"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import FormContainer from "@hydrologiq/shared/chakra/custom/FormContainer"

interface ActivityCreateAttributesProps extends ActivityCreateProps {
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  activities: gqlTypes.Activity[]
  allowAnyAsset?: boolean
  onActivityCreated?: () => void
}

const ActivityCreateAttributes = ({
  order,
  asset,
  activities,
  allowAnyAsset,
  onScreenChange,
  onBack,
  onClose,
  onActivityCreated,
}: ActivityCreateAttributesProps) => {
  const createFormData = useCreateFormData()
  const { updateCreateFormField, setIsCreating } = useActivityWorkflowActions()
  const latestBatch = useLatestBatch()
  const attributesRef = useRef<ActivityAttributesConfigurableRef>(null)
  const [error, setError] = React.useState<string | null>(null)

  const [createActivity, { loading }] = useMutationWithEndpoint(
    gqlTypes.CreateActivityDocument,
    {
      // Refetch the linkable-activity queries so an open link picker includes the new activity.
      refetchQueries: [
        "GetSuggestedFulfilments",
        "AssetAvailabilityCalendar",
        "GetLinkableActivities",
        "GetLinkableActivitiesByDates",
      ],
      awaitRefetchQueries: false,
    },
    "primary"
  )

  // Extract asset IDs for mock activity
  const assetIds = useMemo(
    () =>
      createFormData?.availableAssets
        ? createFormData.availableAssets.map((asset) => asset.id)
        : asset
        ? [asset.id]
        : [],
    [createFormData?.availableAssets, asset]
  )

  // Create mock activity for attribute configuration
  const mockActivity = useMemo<gqlTypes.Activity>(() => {
    if (!createFormData) {
      return {
        __typename: "ActivityType",
        id: "temp-new-activity",
        order: order ? (order as any as gqlTypes.OrderType) : null,
        kind: gqlTypes.ActivityKind.Filling,
        created_by: "",
        created_at: new Date().toISOString(),
        assets: [],
        attributes: [],
        events: [],
        status: gqlTypes.ActivityStatus.Started,
      }
    }

    const orderToUse = createFormData.kind === gqlTypes.ActivityKind.Fulfilment ? createFormData.order : order
    return {
      __typename: "ActivityType",
      id: "temp-new-activity",
      order: orderToUse ? (orderToUse as any as gqlTypes.OrderType) : null,
      kind: createFormData.kind,
      created_by: "",
      created_at: new Date().toISOString(),
      assets: assetIds.map((id) => ({ id } as gqlTypes.AssetBasic)),
      attributes: [],
      events: [],
      status: gqlTypes.ActivityStatus.Started,
      quantity_scheduled: createFormData.quantity_scheduled,
    }
  }, [createFormData, order, asset, assetIds])

  const handleSubmit = async (): Promise<boolean> => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      if (!createFormData) {
        setError("Form data is missing")
        return false
      }

      // Validate all attribute fields before submitting
      if (attributesRef.current) {
        const isValid = attributesRef.current.validateAll()
        if (!isValid) {
          setError("Please fix validation errors before submitting")
          showErrorToast("Please fix validation errors")
          return false
        }
      }

      // Show loading toast
      loadingToastId = showLoadingToast("Creating Activity", "Adding activity...")
      setIsCreating(true)

      // Get asset ID from attribute values
      const assetIdOrName = createFormData.attributes.asset
      let assetId: string | undefined = undefined

      if (allowAnyAsset) {
        assetId = assetIdOrName || undefined
      } else {
        if (assetIdOrName && assetIds.includes(assetIdOrName)) {
          assetId = assetIdOrName
        }
      }

      const activityAssets = assetId ? [{ asset: assetId }] : []
      const attributeInputs: gqlTypes.ActivityAttributeInput[] = []

      if (assetId) {
        if (createFormData.attributes.pressure) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Pressure,
            value: createFormData.attributes.pressure,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.quantity) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Quantity,
            value: createFormData.attributes.quantity,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.temperature) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Temperature,
            value: createFormData.attributes.temperature,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.fill_level) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.FillLevel,
            value: createFormData.attributes.fill_level,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.site) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Site,
            value: createFormData.attributes.site,
            site: createFormData.attributes.site,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.source) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Source,
            value: createFormData.attributes.source,
            site: createFormData.attributes.source,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.destination) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Destination,
            value: createFormData.attributes.destination,
            site: createFormData.attributes.destination,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.batch) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Batch,
            value: createFormData.attributes.batch,
            batch: createFormData.attributes.batch,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }

        if (createFormData.attributes.note) {
          attributeInputs.push({
            attribute: gqlTypes.ActivityAttributeKind.Note,
            value: createFormData.attributes.note,
            event_kind: createFormData.status,
            asset: assetId,
          })
        }
      }

      let vars: gqlTypes.CreateActivityMutationVariables = {
        kind: createFormData.kind,
        assets: activityAssets,
        event_status: createFormData.status,
        attributes: attributeInputs,
      }

      // Use order from form data for Fulfilment activities
      const orderToUse = createFormData.kind === gqlTypes.ActivityKind.Fulfilment ? createFormData.order : order
      if (orderToUse) {
        vars.order = orderToUse.id
      } else if (createFormData.kind === gqlTypes.ActivityKind.Fulfilment) {
        const errorMessage = "Please select an order for Fulfilment activities"
        setError(errorMessage)
        showErrorToast("Validation Error", errorMessage)
        return false
      }

      if (createFormData.links && createFormData.links.length > 0) {
        const linksInput: gqlTypes.ActivityLinkInput[] = createFormData.links.map((link) => ({
          target_activity_id: link.targetActivityId,
          link_type: link.linkType,
        }))
        vars.links = linksInput
      }

      // Validate and convert dates if provided
      const tz = zone(IANATimeZone())
      if (createFormData.start && createFormData.end) {
        try {
          const start = new DateTime(createFormData.start, "YYYY-MM-DD'T'HH:mm", tz)
          const end = new DateTime(createFormData.end, "YYYY-MM-DD'T'HH:mm", tz)

          if (end.valueOf() <= start.valueOf()) {
            const errorMessage = "End date/time must be after start date/time"
            setError(errorMessage)
            showErrorToast("Validation Error", errorMessage)
            return false
          }

          vars.start = start.toZone(utc()).toIsoString()
          vars.end = end.toZone(utc()).toIsoString()
        } catch (error) {
          const errorMessage = "Invalid date/time format. Please check your dates."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      } else if (createFormData.start) {
        try {
          const start = new DateTime(createFormData.start, "YYYY-MM-DD'T'HH:mm", tz).toZone(utc())
          vars.start = start.toIsoString()
        } catch (error) {
          const errorMessage = "Invalid start date/time format."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      } else if (createFormData.end) {
        try {
          const end = new DateTime(createFormData.end, "YYYY-MM-DD'T'HH:mm", tz).toZone(utc())
          vars.end = end.toIsoString()
        } catch (error) {
          const errorMessage = "Invalid end date/time format."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      }

      const result = await createActivity({
        variables: vars,
      })

      // Check for GraphQL errors first
      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message || "Failed to create activity. Please try again."
        setError(errorMessage)
        showErrorToast("Creation Failed", errorMessage)
        return false
      }

      if (result.data?.createActivity) {
        const activityLabel = formatActivityKind(createFormData.kind)
        const orderName = orderToUse?.name || order?.name
        showSuccessToast(
          "Activity Created",
          orderName
            ? `Activity "${activityLabel}" has been successfully added to order "${orderName}".`
            : `Activity "${activityLabel}" has been successfully created.`
        )

        // Call success callbacks
        if (onActivityCreated) {
          onActivityCreated()
        }
        onClose()
        return true
      } else {
        const errorMessage = "Failed to create activity. Please try again."
        setError(errorMessage)
        showErrorToast("Creation Failed", errorMessage)
        return false
      }
    } catch (error: any) {
      const errorMessage = error?.message || ""

      if (errorMessage.includes("Conflict") || errorMessage.includes("already exists")) {
        const conflictError = "A conflict occurred while creating the activity. Please try again."
        setError(conflictError)
        showErrorToast("Creation Conflict", "A conflict occurred. Please try again.")
      } else {
        setError("Unable to create activity. Please check your inputs and try again.")
        showErrorToast("Creation Failed", "Unable to create activity. Please try again.")
      }
      return false
    } finally {
      if (loadingToastId) {
        dismissToast(loadingToastId)
      }
      setIsCreating(false)
    }
  }

  if (!createFormData) return null

  return (
    <ActivityCreateScreenWrapper>
      <FormContainer actionType="create" loading={loading} canSubmit={true} onSubmit={handleSubmit}>
        {error && <ErrorAlert title="Activity Creation Error" message={error} onClose={() => setError(null)} />}

        {/* Attributes Section */}
        <VStack gap={4} align="stretch">
          <Text fontSize="sm">
            Provide additional context for this <strong>{formatActivityKind(createFormData.kind).toLowerCase()}</strong>
            .
          </Text>

          <ActivityAttributesConfigurable
            ref={attributesRef}
            order={createFormData.kind === gqlTypes.ActivityKind.Fulfilment ? createFormData.order : order}
            asset={asset}
            availableAssets={createFormData.availableAssets}
            activities={activities}
            activity={mockActivity}
            eventKind={createFormData.status}
            values={createFormData.attributes}
            onChange={(values) => {
              if (typeof values === "function") {
                updateCreateFormField("attributes", values(createFormData.attributes))
              } else {
                updateCreateFormField("attributes", values)
              }
            }}
            onNavigateToBatchCreation={() => onScreenChange(ActivityCreateScreen.CreateBatch)}
            newlyCreatedBatch={latestBatch}
            allowAnyAsset={allowAnyAsset}
          />
        </VStack>
      </FormContainer>
    </ActivityCreateScreenWrapper>
  )
}

export default ActivityCreateAttributes
